import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAlertas, atenderAlerta } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Bell, Warning, CalendarX, BookOpen, Check, Clock
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';

const AlertasPage = () => {
  const { user } = useAuth();
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pendientes');
  const [showModal, setShowModal] = useState(false);
  const [selectedAlerta, setSelectedAlerta] = useState(null);
  const [notas, setNotas] = useState('');
  const [filter, setFilter] = useState({ tipo: '', turno: '' });

  const fetchAlertas = useCallback(async () => {
    setLoading(true);
    try {
      const params = { atendida: activeTab === 'atendidas' };
      if (filter.tipo && filter.tipo !== 'all') params.tipo = filter.tipo;
      if (filter.turno && filter.turno !== 'all') params.turno = filter.turno;
      
      const response = await getAlertas(params);
      setAlertas(response.data);
    } catch (error) {
      toast.error('Error al cargar alertas');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filter]);

  useEffect(() => {
    fetchAlertas();
  }, [fetchAlertas]);

  const handleAtender = async () => {
    if (!selectedAlerta) return;
    try {
      await atenderAlerta(selectedAlerta.id, notas);
      toast.success('Alerta atendida');
      setShowModal(false);
      setNotas('');
      fetchAlertas();
    } catch (error) {
      toast.error('Error al atender alerta');
    }
  };

  const openAtenderModal = (alerta) => {
    setSelectedAlerta(alerta);
    setNotas('');
    setShowModal(true);
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'faltas_acumuladas':
        return <CalendarX size={20} className="text-red-500" weight="fill" />;
      case 'patre_consecutivo':
        return <BookOpen size={20} className="text-amber-500" weight="fill" />;
      default:
        return <Warning size={20} className="text-amber-500" weight="fill" />;
    }
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      faltas_acumuladas: 'Faltas Acumuladas',
      patre_consecutivo: 'PATRE Consecutivo'
    };
    return labels[tipo] || tipo.replace(/_/g, ' ');
  };

  const pendientes = alertas.filter(a => !a.atendida);
  const atendidas = alertas.filter(a => a.atendida);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alertas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Alertas</h1>
          <p className="text-muted-foreground">Sistema de alertas automáticas</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800">
          <CardContent className="p-4 flex items-center gap-3">
            <Bell size={24} className="text-red-600" weight="fill" />
            <div>
              <p className="text-2xl font-bold text-red-700 dark:text-red-300">
                {alertas.filter(a => !a.atendida).length}
              </p>
              <p className="text-sm text-red-600 dark:text-red-400">Pendientes</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CalendarX size={24} className="text-primary" weight="duotone" />
            <div>
              <p className="text-2xl font-bold">
                {alertas.filter(a => a.tipo === 'faltas_acumuladas' && !a.atendida).length}
              </p>
              <p className="text-sm text-muted-foreground">Por faltas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <BookOpen size={24} className="text-primary" weight="duotone" />
            <div>
              <p className="text-2xl font-bold">
                {alertas.filter(a => a.tipo === 'patre_consecutivo' && !a.atendida).length}
              </p>
              <p className="text-sm text-muted-foreground">Por PATRE</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Tipo de alerta</Label>
              <Select value={filter.tipo} onValueChange={(v) => setFilter(p => ({ ...p, tipo: v }))}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="faltas_acumuladas">Faltas Acumuladas</SelectItem>
                  <SelectItem value="patre_consecutivo">PATRE Consecutivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Turno</Label>
              <Select value={filter.turno} onValueChange={(v) => setFilter(p => ({ ...p, turno: v }))}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="matutino">Matutino</SelectItem>
                  <SelectItem value="vespertino">Vespertino</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pendientes" className="flex items-center gap-2">
            <Clock size={16} />
            Pendientes
            {pendientes.length > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {pendientes.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="atendidas" className="flex items-center gap-2">
            <Check size={16} />
            Atendidas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pendientes" className="mt-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : alertas.filter(a => !a.atendida).length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Bell size={48} className="mx-auto mb-3 opacity-50" />
              <p>No hay alertas pendientes</p>
            </div>
          ) : (
            <div className="space-y-4">
              {alertas.filter(a => !a.atendida).map((alerta, index) => (
                <Card 
                  key={alerta.id} 
                  className="border-l-4 border-l-destructive stagger-item"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        {getTipoIcon(alerta.tipo)}
                        <div className="flex-1">
                          <p className="font-medium">{alerta.mensaje}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {getTipoLabel(alerta.tipo)}
                            </Badge>
                            {alerta.matricula && (
                              <Badge variant="secondary" className="text-xs">
                                Mat: {alerta.matricula}
                              </Badge>
                            )}
                            {alerta.grado && (
                              <Badge variant="secondary" className="text-xs">
                                {alerta.grado}° {alerta.grupo}
                              </Badge>
                            )}
                            {alerta.turno && (
                              <Badge variant="secondary" className="text-xs capitalize">
                                {alerta.turno}
                              </Badge>
                            )}
                          </div>
                          {alerta.datos_adicionales && (
                            <div className="mt-2 text-sm text-muted-foreground">
                              {alerta.tipo === 'faltas_acumuladas' && alerta.datos_adicionales.faltas && (
                                <p>Faltas: {alerta.datos_adicionales.faltas} | Periodo: {alerta.datos_adicionales.periodo}</p>
                              )}
                              {alerta.tipo === 'patre_consecutivo' && alerta.datos_adicionales.materia && (
                                <p>Materia: {alerta.datos_adicionales.materia}</p>
                              )}
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground mt-2">
                            {alerta.fecha_creacion && format(new Date(alerta.fecha_creacion), "d 'de' MMMM, yyyy HH:mm", { locale: es })}
                          </p>
                        </div>
                      </div>
                      <Button onClick={() => openAtenderModal(alerta)} data-testid={`atender-${alerta.id}`}>
                        Atender
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="atendidas" className="mt-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : alertas.filter(a => a.atendida).length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Check size={48} className="mx-auto mb-3 opacity-50" />
              <p>No hay alertas atendidas</p>
            </div>
          ) : (
            <div className="space-y-4">
              {alertas.filter(a => a.atendida).map((alerta, index) => (
                <Card 
                  key={alerta.id} 
                  className="border-l-4 border-l-green-500 stagger-item"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Check size={20} className="text-green-500" weight="bold" />
                      <div className="flex-1">
                        <p className="font-medium">{alerta.mensaje}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {getTipoLabel(alerta.tipo)}
                          </Badge>
                          {alerta.matricula && (
                            <Badge variant="secondary" className="text-xs">
                              Mat: {alerta.matricula}
                            </Badge>
                          )}
                        </div>
                        {alerta.notas_atencion && (
                          <p className="text-sm mt-2 p-2 bg-muted rounded">
                            <strong>Notas:</strong> {alerta.notas_atencion}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          Atendida: {alerta.fecha_atencion && format(new Date(alerta.fecha_atencion), "d 'de' MMMM, yyyy HH:mm", { locale: es })}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Atender Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Atender Alerta</DialogTitle>
          </DialogHeader>
          {selectedAlerta && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  {getTipoIcon(selectedAlerta.tipo)}
                  <span className="font-medium">{getTipoLabel(selectedAlerta.tipo)}</span>
                </div>
                <p className="text-sm">{selectedAlerta.mensaje}</p>
              </div>
              <div>
                <Label>Notas de atención (opcional)</Label>
                <Textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  placeholder="Acciones tomadas, observaciones..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAtender}>
              <Check size={16} className="mr-2" />
              Marcar como atendida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AlertasPage;
